import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  Input,
  InputNumber,
  Select,
  Switch,
  Tag,
  message,
} from "antd";
import {
  MinusOutlined,
  PlusOutlined,
  DeleteOutlined,
  ShoppingCartOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import { getPOSSaleById } from "../../apis/pos";
import {
  createSaleReturn,
  getSaleReturnById,
  getSaleReturns,
  RefundMode,
} from "../../apis/saleReturns";
import { useNotification } from "../../NotificationContext";

const { Option } = Select;
const { TextArea } = Input;

interface ReturnCartItem {
  id: string;
  saleItemId: number;
  pharmacyItemId: number;
  name: string;
  sku: string;
  batchNumber?: string;
  expiryDate?: string;
  maxQty: number;
  qtyReturned: number;
  unitRate: number;
  taxPercentage: number;
  amount: number;
  restockFlag: boolean;
  selected: boolean;
}

const CreateCreditNote: React.FC = () => {
  const { saleId } = useParams<{ saleId: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sale, setSale] = useState<any>(null);
  const [items, setItems] = useState<ReturnCartItem[]>([]);
  const [returnDate, setReturnDate] = useState<Date>(new Date());
  const [reason, setReason] = useState("");
  const [refundMode, setRefundMode] = useState<RefundMode>("cash");
  const [isGstAdjustable, setIsGstAdjustable] = useState(true);
  const [creditNoteNo, setCreditNoteNo] = useState("");
  const [creditNoteDate, setCreditNoteDate] = useState<Date | null>(new Date());

  useEffect(() => {
    if (!saleId) return;
    const id = parseInt(saleId, 10);
    if (Number.isNaN(id)) return;
    loadSale(id);
  }, [saleId]);

  const loadSale = async (id: number) => {
    setLoading(true);
    try {
      const [saleRes, returnsRes]: any[] = await Promise.all([
        getPOSSaleById(id),
        getSaleReturns({ sale_id: id, per_page: 200 }),
      ]);

      const saleData = saleRes?.data ?? saleRes;
      if (!saleData) {
        showNotification("error", "Sale not found", "", "topRight");
        navigate("/pharmacy/pos/list");
        return;
      }
      if (saleData.status === "cancelled") {
        showNotification("error", "Cannot return a cancelled sale", "", "topRight");
        navigate("/pharmacy/pos/list");
        return;
      }

      const existingReturns = returnsRes?.data?.data || returnsRes?.data || [];
      const returnedBySaleItem: Record<number, number> = {};
      const returnRows = Array.isArray(existingReturns) ? existingReturns : [];
      const details = await Promise.all(
        returnRows.map(async (ret: any) => {
          try {
            const detailRes: any = await getSaleReturnById(ret.id);
            return detailRes?.data ?? detailRes;
          } catch {
            return null;
          }
        })
      );
      details.forEach((ret: any) => {
        (ret?.items || []).forEach((ri: any) => {
          const key = Number(ri.saleItemId ?? ri.sale_item_id);
          if (!key) return;
          returnedBySaleItem[key] =
            (returnedBySaleItem[key] || 0) + Number(ri.qtyReturned ?? ri.qty_returned ?? 0);
        });
      });

      const saleItems = saleData.items ?? saleData.saleItems ?? saleData.sale_items ?? [];
      const cartItems: ReturnCartItem[] = saleItems
        .map((item: any, idx: number) => {
          const pharmacyItem = item.pharmacyItem ?? item.pharmacy_item;
          const batch = item.batch;
          const soldQty = Number(item.quantity ?? 0);
          const alreadyReturned = returnedBySaleItem[Number(item.id)] || 0;
          const maxQty = Math.max(0, soldQty - alreadyReturned);
          const netAmount = Number(item.netAmount ?? item.net_amount ?? 0);
          const unitRate = soldQty > 0 ? netAmount / soldQty : 0;

          return {
            id: `return-${item.id ?? idx}`,
            saleItemId: Number(item.id),
            pharmacyItemId: Number(
              pharmacyItem?.id ?? item.pharmacyItemId ?? item.pharmacy_item_id
            ),
            name: pharmacyItem?.name ?? item.name ?? "-",
            sku: pharmacyItem?.sku ?? item.sku ?? "",
            batchNumber:
              batch?.batchNumber ?? batch?.batch_number ?? item.batchNumber ?? item.batch_no,
            expiryDate: batch?.expiryDate ?? batch?.expiry_date ?? item.expiryDate,
            maxQty,
            qtyReturned: maxQty > 0 ? maxQty : 0,
            unitRate,
            taxPercentage: Number(item.taxPercentage ?? item.tax_percentage ?? 0),
            amount: maxQty > 0 ? unitRate * maxQty : 0,
            restockFlag: Boolean(item.pharmacyBatchId ?? item.pharmacy_batch_id ?? batch?.id),
            selected: maxQty > 0,
          } as ReturnCartItem;
        })
        .filter((item: ReturnCartItem) => item.saleItemId);

      setSale(saleData);
      setItems(cartItems);
    } catch {
      showNotification("error", "Failed to load sale for credit note", "", "topRight");
      navigate("/pharmacy/pos/list");
    } finally {
      setLoading(false);
    }
  };

  const selectedItems = useMemo(
    () => items.filter((item) => item.selected && item.qtyReturned > 0 && item.maxQty > 0),
    [items]
  );

  const totals = useMemo(() => {
    const qty = selectedItems.reduce((sum, item) => sum + item.qtyReturned, 0);
    const amount = selectedItems.reduce((sum, item) => sum + item.amount, 0);
    return { qty, amount };
  }, [selectedItems]);

  const updateItem = (id: string, patch: Partial<ReturnCartItem>) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const next = { ...item, ...patch };
        const qty = Math.min(next.maxQty, Math.max(0, Number(next.qtyReturned) || 0));
        next.qtyReturned = qty;
        next.amount = +(qty * next.unitRate).toFixed(2);
        if (qty === 0) next.selected = false;
        return next;
      })
    );
  };

  const handleSubmit = async () => {
    if (!sale?.id) return;
    if (selectedItems.length === 0) {
      message.warning("Select at least one item to return");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        sale_id: Number(sale.id),
        return_date: format(returnDate, "yyyy-MM-dd HH:mm:ss"),
        reason: reason || undefined,
        is_gst_adjustable: isGstAdjustable,
        credit_note_no: creditNoteNo || null,
        credit_note_date: creditNoteNo
          ? format(creditNoteDate || new Date(), "yyyy-MM-dd")
          : null,
        refund_mode: refundMode,
        items: selectedItems.map((item) => ({
          sale_item_id: item.saleItemId,
          qty_returned: item.qtyReturned,
          restock_flag: item.restockFlag,
        })),
      };

      await createSaleReturn(payload);
      message.success("Credit note created successfully");
      navigate("/pharmacy/credit-notes");
    } catch (err: any) {
      const details =
        err?.response?.data?.details?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create credit note";
      message.error(details);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[70vh] text-gray-500">
        Loading sale items...
      </div>
    );
  }

  const patientName = sale?.patient
    ? `${sale.patient.firstName || sale.patient.first_name || ""} ${
        sale.patient.lastName || sale.patient.last_name || ""
      }`.trim() || "Walk-in"
    : "Walk-in";

  return (
    <div className="h-[calc(100vh-4rem)] bg-gray-50 flex flex-col">
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Create Credit Note</h1>
          <p className="text-sm text-gray-500">
            Sale {sale?.saleNumber || sale?.sale_number || `#${sale?.id}`} · {patientName}
          </p>
        </div>
        <Button icon={<CloseOutlined />} onClick={() => navigate("/pharmacy/credit-notes")}>
          Close
        </Button>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 overflow-hidden">
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="flex items-center gap-2 font-medium text-gray-800">
              <ShoppingCartOutlined />
              Sale Items (return only from this bill)
            </div>
            <Tag color="blue">{items.filter((i) => i.maxQty > 0).length} returnable</Tag>
          </div>

          <div className="flex-1 overflow-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No items on this sale</div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">Return</th>
                    <th className="px-3 py-2 text-left">Item</th>
                    <th className="px-3 py-2 text-left">Batch</th>
                    <th className="px-3 py-2 text-right">Available</th>
                    <th className="px-3 py-2 text-center">Qty</th>
                    <th className="px-3 py-2 text-right">Rate</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-center">Restock</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((item) => {
                    const disabled = item.maxQty <= 0;
                    return (
                      <tr key={item.id} className={disabled ? "bg-gray-50 opacity-60" : ""}>
                        <td className="px-3 py-3">
                          <Checkbox
                            checked={item.selected && !disabled}
                            disabled={disabled}
                            onChange={(e) =>
                              updateItem(item.id, {
                                selected: e.target.checked,
                                qtyReturned: e.target.checked
                                  ? item.qtyReturned || item.maxQty
                                  : 0,
                              })
                            }
                          />
                        </td>
                        <td className="px-3 py-3">
                          <div className="font-medium text-gray-900">{item.name}</div>
                          <div className="text-xs text-gray-400">{item.sku}</div>
                          {disabled ? <Tag color="red">Fully returned</Tag> : null}
                        </td>
                        <td className="px-3 py-3 text-gray-700">
                          {item.batchNumber || "-"}
                          {item.expiryDate ? (
                            <div className="text-xs text-gray-400">
                              Exp {format(new Date(item.expiryDate), "dd MMM yyyy")}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-3 py-3 text-right">{item.maxQty}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="small"
                              icon={<MinusOutlined />}
                              disabled={disabled || !item.selected || item.qtyReturned <= 1}
                              onClick={() =>
                                updateItem(item.id, { qtyReturned: item.qtyReturned - 1 })
                              }
                            />
                            <InputNumber
                              min={0}
                              max={item.maxQty}
                              value={item.qtyReturned}
                              disabled={disabled || !item.selected}
                              onChange={(value) =>
                                updateItem(item.id, { qtyReturned: Number(value) || 0 })
                              }
                              className="w-16"
                              size="small"
                            />
                            <Button
                              size="small"
                              icon={<PlusOutlined />}
                              disabled={
                                disabled || !item.selected || item.qtyReturned >= item.maxQty
                              }
                              onClick={() =>
                                updateItem(item.id, { qtyReturned: item.qtyReturned + 1 })
                              }
                            />
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right">₹{item.unitRate.toFixed(2)}</td>
                        <td className="px-3 py-3 text-right font-medium">
                          ₹{item.amount.toFixed(2)}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <Switch
                            size="small"
                            checked={item.restockFlag}
                            disabled={disabled || !item.selected}
                            onChange={(checked) =>
                              updateItem(item.id, { restockFlag: checked })
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 flex flex-col gap-4 overflow-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Return Date</label>
            <DatePicker
              selected={returnDate}
              onChange={(date: Date | null) => date && setReturnDate(date)}
              showTimeSelect
              dateFormat="dd MMM yyyy h:mm aa"
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Refund Mode</label>
            <Select
              className="w-full"
              value={refundMode}
              onChange={(value: RefundMode) => setRefundMode(value)}
            >
              <Option value="cash">Cash</Option>
              <Option value="credit">Credit</Option>
              <Option value="adjust_against_next_bill">Adjust against next bill</Option>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">GST Adjustable</span>
            <Switch checked={isGstAdjustable} onChange={setIsGstAdjustable} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Credit Note No (optional)
            </label>
            <Input
              value={creditNoteNo}
              onChange={(e) => setCreditNoteNo(e.target.value)}
              placeholder="CN-1001"
            />
          </div>

          {creditNoteNo ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Credit Note Date
              </label>
              <DatePicker
                selected={creditNoteDate}
                onChange={(date: Date | null) => setCreditNoteDate(date)}
                dateFormat="dd MMM yyyy"
                className="w-full border rounded-md px-3 py-2"
              />
            </div>
          ) : null}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <TextArea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this being returned?"
            />
          </div>

          <div className="mt-auto border-t pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Items selected</span>
              <span className="font-medium">{selectedItems.length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Qty returning</span>
              <span className="font-medium">{totals.qty}</span>
            </div>
            <div className="flex justify-between text-base font-semibold">
              <span>Credit Amount</span>
              <span>₹{totals.amount.toFixed(2)}</span>
            </div>

            <Button
              type="primary"
              block
              size="large"
              className="!bg-primary"
              loading={submitting}
              disabled={selectedItems.length === 0}
              onClick={handleSubmit}
            >
              Create Credit Note
            </Button>
            <Button
              block
              icon={<DeleteOutlined />}
              onClick={() => navigate("/pharmacy/pos/list")}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCreditNote;
